/**
 * Zafco Business Vertical Contact Channel Configuration
 * ─────────────────────────────────────────────────────────────────────
 * Single source of truth for phone, WhatsApp, email, and conversion-
 * attribution metadata across the two business verticals:
 *
 *   1. Overseas Recruitment, Work Visa, PR & Immigration  → "recruitment"
 *   2. Study Abroad                                       → "study_abroad"
 *
 * IMPORTANT (compliance / Google Ads policy):
 *   • The vertical for a given page is decided ONLY by the URL pathname.
 *   • There is NO user-agent, IP, crawler, cookie, Ads-source, or
 *     visitor-class detection anywhere in this file or its consumer hook.
 *   • The exact same HTML is served to Google's crawler, Ads reviewers,
 *     and real visitors for the same URL — deterministic & cacheable.
 *
 * To add a new vertical in future:
 *   1. Append a new key to VERTICALS below.
 *   2. Append a new rule to ROUTE_TO_VERTICAL.
 *   No other change is required across the codebase — every shared
 *   component reads via `useContactChannel()`.
 */

export const VERTICAL_RECRUITMENT  = 'recruitment';
export const VERTICAL_STUDY_ABROAD = 'study_abroad';

// ────────────────────────────────────────────────────────────────────
// Service catalogues — centralised, vertical-scoped.
//
// Single source of truth used by every consultation / inline lead form
// across the site (ConsultationForm and any future variant). Adding a
// new service = append to the right array; the dropdowns on all SA or
// recruitment pages pick it up automatically — no form-level edits.
//
// IMPORTANT: keep these strings stable. They are persisted into Mongo
// (`service_type` column), forwarded to Zoho CRM (`Service_Interest`),
// and emitted on every GA4 / Google Ads conversion event. Renaming a
// service breaks historical reports.
// ────────────────────────────────────────────────────────────────────
export const SERVICE_CATALOGUE = {
  [VERTICAL_RECRUITMENT]: [
    'Work Visa Recruitment',
    'Skilled Migration',
    'Business/Investor Placement',
    'Job Seeker Assistance',
    'Visit Visa Support',
    'PR / Immigration Advisory',
  ],
  [VERTICAL_STUDY_ABROAD]: [
    'Study Abroad Placement',
    'University Shortlisting',
    'Scholarship Guidance',
    'Student Visa Support',
    'Profile Evaluation',
    'Career Counselling',
  ],
};

export const VERTICALS = {
  [VERTICAL_RECRUITMENT]: {
    key:               VERTICAL_RECRUITMENT,
    label:             'Overseas Recruitment & Immigration',
    leadServiceTag:    'Overseas Placement',                 // CRM / source tag
    // Display variants — pick the right one at the call site.
    phoneRaw:          '919958848998',                       // for wa.me / tel: links (no spaces, no plus)
    phoneE164:         '+919958848998',                      // for tel: links + microdata
    phoneDisplay:      '+91 99588 48998',                    // for human-visible UI
    phoneDisplayShort: '+91-9958848998',                     // compact UI
    whatsappRaw:       '919958848998',
    whatsappE164:      '+919958848998',
    whatsappDisplay:   '+91 99588 48998',
    email:             'info@zafco.in',
    // Default WhatsApp opener message
    whatsappGreeting:  'Hello Zafco, I need help with overseas recruitment & visa services.',
    // Analytics: vertical-specific Google Ads conversion-label env keys
    // (separate Ads conversion actions per vertical per the user's spec).
    adsLabelEnv: {
      form_submit:        'REACT_APP_GADS_CONVERSION_LABEL_FORM_SUBMIT_RC',
      eligibility_submit: 'REACT_APP_GADS_CONVERSION_LABEL_ELIGIBILITY_RC',
      counsellor_booking: 'REACT_APP_GADS_CONVERSION_LABEL_BOOKING_RC',
      primary_cta_click:  'REACT_APP_GADS_CONVERSION_LABEL_CTA_CLICK_RC',
      whatsapp_click:     'REACT_APP_GADS_CONVERSION_LABEL_WHATSAPP_RC',
      call_click:         'REACT_APP_GADS_CONVERSION_LABEL_CALL_CLICK_RC',
    },
  },
  [VERTICAL_STUDY_ABROAD]: {
    key:               VERTICAL_STUDY_ABROAD,
    label:             'Zafco Study Abroad',
    leadServiceTag:    'Study Abroad',
    phoneRaw:          '919990533327',
    phoneE164:         '+919990533327',
    phoneDisplay:      '+91 99905 33327',
    phoneDisplayShort: '+91-9990533327',
    whatsappRaw:       '919990533327',
    whatsappE164:      '+919990533327',
    whatsappDisplay:   '+91 99905 33327',
    email:             'studyabroad@zafco.in',
    whatsappGreeting:  'Hello Zafco Study Abroad, I would like guidance on universities & student visa.',
    adsLabelEnv: {
      form_submit:        'REACT_APP_GADS_CONVERSION_LABEL_FORM_SUBMIT_SA',
      eligibility_submit: 'REACT_APP_GADS_CONVERSION_LABEL_ELIGIBILITY_SA',
      counsellor_booking: 'REACT_APP_GADS_CONVERSION_LABEL_BOOKING_SA',
      primary_cta_click:  'REACT_APP_GADS_CONVERSION_LABEL_CTA_CLICK_SA',
      whatsapp_click:     'REACT_APP_GADS_CONVERSION_LABEL_WHATSAPP_SA',
      call_click:         'REACT_APP_GADS_CONVERSION_LABEL_CALL_CLICK_SA',
    },
  },
};

/**
 * Route-prefix rules. Order matters — first match wins.
 * Add new verticals by prepending a new rule (more specific first).
 *
 * Pure static logic — no headers, cookies, or visitor signals.
 */
const ROUTE_TO_VERTICAL = [
  { prefix: '/study-abroad', vertical: VERTICAL_STUDY_ABROAD },
  // Future: { prefix: '/saudi-employer-portal', vertical: VERTICAL_RECRUITMENT },
  // Default fallback handled below.
];

const DEFAULT_VERTICAL = VERTICAL_RECRUITMENT;

/**
 * Resolve which vertical owns a given pathname.
 * Deterministic, pure function — same input always returns same output.
 *
 * @param {string} pathname — e.g. '/study-abroad/uk/'
 * @returns {string} 'recruitment' | 'study_abroad'
 */
export function resolveVerticalFromPath(pathname) {
  if (typeof pathname !== 'string' || !pathname) return DEFAULT_VERTICAL;
  // Normalise: ensure leading slash, strip trailing slash for prefix check.
  const p = pathname.startsWith('/') ? pathname : `/${pathname}`;
  for (const rule of ROUTE_TO_VERTICAL) {
    if (p === rule.prefix || p.startsWith(`${rule.prefix}/`) || p.startsWith(`${rule.prefix}?`) || p.startsWith(`${rule.prefix}#`)) {
      return rule.vertical;
    }
  }
  return DEFAULT_VERTICAL;
}

/**
 * Build a wa.me deep link for a given vertical.
 * @param {object} channel — a `VERTICALS[verticalKey]` object
 * @param {string} [text]  — pre-filled message (defaults to channel greeting)
 */
export function buildWhatsAppUrl(channel, text) {
  const msg = encodeURIComponent(text || channel.whatsappGreeting);
  return `https://wa.me/${channel.whatsappRaw}?text=${msg}`;
}

/**
 * Build a tel: link for a given vertical.
 */
export function buildTelUrl(channel) {
  return `tel:${channel.phoneE164}`;
}

export default VERTICALS;
